/**
 * DayCard — eine Tageskarte (Vormittag + Nachmittag).
 *
 * `Card` (elevation 0) mit Kopf (Wochentag + Datum). Mobil stehen Hin- und
 * Rückweg untereinander, ab `md` nebeneinander. Am Fuß ein voller
 * Kommentar-Button (`ChatBubbleOutline`), der den Kommentar-Drawer öffnet.
 * Behält `data-testid="day-card"` und erhält `id="day-N"` für die mobile
 * Tagesübersicht (DayStrip-Anker).
 */

import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import ChatBubbleOutline from "@mui/icons-material/ChatBubbleOutline";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useI18n } from "../../i18n/I18nProvider";
import TripSection from "./TripSection";
import { DAY_DATE_OPTS } from "./helpers";
import type { DayView, SimpleChild, TripAction } from "./types";

export default function DayCard({
  index,
  label,
  day,
  displayedUserId,
  displayedName,
  isSelf,
  adminView,
  displayedChildren,
  disabled,
  onAction,
  onOpenDetails,
  onOpenPersonDetails,
  onOpenComments,
}: {
  index: number;
  label: string;
  day: DayView;
  displayedUserId: string | undefined;
  displayedName: string;
  isSelf: boolean;
  adminView: boolean;
  /** Kinder der angezeigten Person (eigene oder, im Admin-Modus, fremde). */
  displayedChildren: SimpleChild[];
  disabled: boolean;
  onAction: (action: TripAction) => void;
  onOpenDetails: (trip: DayView["morning"]) => void;
  onOpenPersonDetails: (trip: DayView["morning"], userId: string, name: string) => void;
  onOpenComments: () => void;
}) {
  const { t, formatDate } = useI18n();
  const theme = useTheme();
  const wide = useMediaQuery(theme.breakpoints.up("md"));

  const noteCount = day.notes.length;

  return (
    <Card
      data-testid="day-card"
      id={`day-${index}`}
      elevation={0}
      sx={{ mx: 2, mt: 2, scrollMarginTop: "200px" }}
    >
      <Stack direction="row" alignItems="baseline" gap={1} px={2} pt={1.75}>
        <Typography variant="h6" fontWeight={700}>
          {label}
        </Typography>
        <Typography color="text.secondary">
          {formatDate(day.date, DAY_DATE_OPTS)}
        </Typography>
      </Stack>

      <Stack direction={wide ? "row" : "column"}>
        <TripSection
          trip={day.morning}
          wide={wide}
          displayedUserId={displayedUserId}
          displayedName={displayedName}
          isSelf={isSelf}
          adminView={adminView}
          personChildren={displayedChildren}
          disabled={disabled}
          onAction={onAction}
          onOpenDetails={() => onOpenDetails(day.morning)}
          onOpenPersonDetails={(uid, name) => onOpenPersonDetails(day.morning, uid, name)}
        />
        <TripSection
          trip={day.afternoon}
          wide={wide}
          displayedUserId={displayedUserId}
          displayedName={displayedName}
          isSelf={isSelf}
          adminView={adminView}
          personChildren={displayedChildren}
          disabled={disabled}
          onAction={onAction}
          onOpenDetails={() => onOpenDetails(day.afternoon)}
          onOpenPersonDetails={(uid, name) => onOpenPersonDetails(day.afternoon, uid, name)}
        />
      </Stack>

      <Divider />
      <Button
        fullWidth
        startIcon={<ChatBubbleOutline />}
        onClick={onOpenComments}
        aria-label={t("weeklyPlan.comments.openAria", { label })}
        sx={{ justifyContent: "flex-start", minHeight: 52, px: 2, color: "text.secondary" }}
      >
        {t("weeklyPlan.comments.label")}
        {noteCount ? ` (${noteCount})` : ""}
      </Button>
    </Card>
  );
}
